import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatService } from '../services/chat.service';
import { AuthService } from '../services/auth.service';
import { ChatInterfaceComponent } from '../chat-interface/chat-interface.component';
import { Subject, takeUntil, fromEvent } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-floating-chat-button',
  standalone: true,
  imports: [CommonModule, ChatInterfaceComponent],
  templateUrl: './floating-chat-button.component.html',
  styleUrls: ['./floating-chat-button.component.css'],
})
export class FloatingChatButtonComponent implements OnInit, OnDestroy {
  @ViewChild('chatContainer') chatContainer!: ElementRef;
  @ViewChild('floatingButton') floatingButton!: ElementRef;
  @ViewChild('chatInterface') chatInterface!: ChatInterfaceComponent;

  private destroy$ = new Subject<void>();
  private welcomeTooltipTimer?: number;
  private autoHideTooltipTimer?: number;
  private serviceUnavailableTooltipTimer?: number;
  private isOpening = false;

  private healthCheckInterval?: number;
  private readonly HEALTH_CHECK_INTERVAL = 5 * 60 * 1000;
  private lastHealthCheck = 0;

  isChatOpen = false;
  isMinimized = false;
  isOnline = true;
  isThinking = false;
  hasUnreadMessages = false;
  showWelcomeTooltip = false;
  isServiceUnavailableTooltipVisible = false;
  isMobile = false;
  isUserLoggedIn = false;

  private lastScrollY = 0;
  private isScrollingDown = false;

  constructor(
    private chatService: ChatService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.checkScreenSize();
    this.setupScrollListener();
    this.setupKeyboardNavigation();
    this.trackUserInteraction();

    this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn: boolean) => {
        this.isUserLoggedIn = isLoggedIn;
        if (!isLoggedIn) {
          this.handleLogout();
        }
      });

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          this.initializeChatService();
          this.initializeWelcomeTooltip();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.welcomeTooltipTimer) {
      clearTimeout(this.welcomeTooltipTimer);
    }

    if (this.autoHideTooltipTimer) {
      clearTimeout(this.autoHideTooltipTimer);
    }

    if (this.serviceUnavailableTooltipTimer) {
      clearTimeout(this.serviceUnavailableTooltipTimer);
    }

    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isChatOpen) {
      this.handleChatClose();
    }

    if (event.altKey && event.key === 'c' && !this.isChatOpen) {
      event.preventDefault();
      this.openChat();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (
      this.isMobile &&
      this.isChatOpen &&
      this.chatContainer &&
      !this.isOpening
    ) {
      const target = event.target as HTMLElement;
      const clickedInside = this.chatContainer.nativeElement.contains(target);
      const clickedButton = this.floatingButton?.nativeElement.contains(target);
      if (!clickedInside && !clickedButton) {
        this.handleChatClose();
      }
    }

    if (
      this.isServiceUnavailableTooltipVisible &&
      this.floatingButton &&
      !this.floatingButton.nativeElement.contains(event.target as Node)
    ) {
      this.hideServiceUnavailableTooltip();
    }
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  private setupAuthListener(): void {
    this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoggedIn: boolean) => {
        if (!isLoggedIn) {
          this.handleLogout();
        }
      });
  }

  private handleLogout(): void {
    this.isChatOpen = false;
    this.isMinimized = false;
    this.hasUnreadMessages = false;

    if (this.isMobile) {
      document.body.style.overflow = '';
    }

    this.hideWelcomeTooltip();

    this.chatService.clearChatHistory();
  }

  private initializeWelcomeTooltip(): void {
    try {
      const hasSeenTooltip = localStorage.getItem('ai-chat-welcome-seen');
      const lastShown = localStorage.getItem('ai-chat-welcome-last-shown');

      if (!hasSeenTooltip) {
        this.welcomeTooltipTimer = window.setTimeout(() => {
          this.showWelcomeTooltip = true;
          try {
            localStorage.setItem(
              'ai-chat-welcome-last-shown',
              Date.now().toString()
            );
          } catch (e) {
            console.warn('Failed to save tooltip timestamp:', e);
          }

          this.autoHideTooltipTimer = window.setTimeout(() => {
            this.hideWelcomeTooltip();
          }, 10000);
        }, 3000);
      } else if (lastShown) {
        const lastShownTime = parseInt(lastShown);
        const oneWeek = 7 * 24 * 60 * 60 * 1000;

        if (Date.now() - lastShownTime > oneWeek) {
          this.welcomeTooltipTimer = window.setTimeout(() => {
            this.showWelcomeTooltip = true;
            try {
              localStorage.setItem(
                'ai-chat-welcome-last-shown',
                Date.now().toString()
              );
            } catch (e) {
              console.warn('Failed to save tooltip timestamp:', e);
            }

            this.autoHideTooltipTimer = window.setTimeout(() => {
              this.hideWelcomeTooltip();
            }, 8000);
          }, 5000);
        }
      }
    } catch (e) {
      console.warn('Failed to access localStorage for welcome tooltip:', e);
      this.welcomeTooltipTimer = window.setTimeout(() => {
        this.showWelcomeTooltip = true;
        this.autoHideTooltipTimer = window.setTimeout(() => {
          this.hideWelcomeTooltip();
        }, 10000);
      }, 3000);
    }
  }

  private initializeChatService(): void {
    this.chatService.isThinking$
      .pipe(takeUntil(this.destroy$))
      .subscribe((thinking: boolean) => {
        this.isThinking = thinking;
        if (thinking && !this.isChatOpen) {
          this.hasUnreadMessages = true;
        }
      });

    this.chatService.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe((online: boolean) => {
        this.isOnline = online;
      });
  }

  private setupScrollListener(): void {
    fromEvent(window, 'scroll')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const currentScrollY = window.scrollY;
        this.isScrollingDown = currentScrollY > this.lastScrollY;
        this.lastScrollY = currentScrollY;

        if (
          this.showWelcomeTooltip &&
          Math.abs(currentScrollY - this.lastScrollY) > 50
        ) {
          this.hideWelcomeTooltip();
        }
      });
  }

  private setupKeyboardNavigation(): void {
    if (this.floatingButton) {
      this.floatingButton.nativeElement.addEventListener(
        'keydown',
        (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.openChat();
          }
        }
      );
    }
  }

  private trackUserInteraction(): void {
    const interactions = ['click', 'scroll', 'keydown'];

    interactions.forEach((eventType) => {
      fromEvent(document, eventType)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.handleUserActivity();
        });
    });
  }

  private handleUserActivity(): void {}

  private performHealthCheck(): void {
    const now = Date.now();
    if (now - this.lastHealthCheck < 60000) {
      return;
    }

    this.lastHealthCheck = now;

    this.chatService.checkHealth().subscribe({
      next: (health) => {
        this.isOnline = health.status === 'healthy';
      },
      error: () => {
        this.isOnline = false;
      },
    });
  }

  getServiceTooltipMessage(): string {
    if (!this.isUserLoggedIn) {
      return 'Please log in to use AI Assistant';
    }
    return 'AI Assistant is currently unavailable';
  }

  getAriaLabel(): string {
    if (!this.isUserLoggedIn) {
      return 'Log in to access AI chat assistant';
    }
    if (!this.isOnline) {
      return 'AI chat assistant is currently unavailable';
    }
    return 'Open AI chat assistant';
  }

  getButtonTitle(): string {
    if (!this.isUserLoggedIn) {
      return 'Please log in to use AI Assistant';
    }
    if (!this.isOnline) {
      return 'AI chat assistant is currently unavailable';
    }
    return 'Open AI chat assistant';
  }

  openChat(): void {
    if (this.isChatOpen) return;

    if (!this.isUserLoggedIn) {
      this.redirectToLogin();
      return;
    }

    if (!this.isOnline) {
      this.showServiceUnavailableTooltip();
      return;
    }

    this.isOpening = true;

    this.isChatOpen = true;
    this.isMinimized = false;
    this.hasUnreadMessages = false;
    this.hideWelcomeTooltip();

    setTimeout(() => {
      this.isOpening = false;
    }, 500);

    setTimeout(() => {
      if (this.chatInterface) {
        const messageInput = document.querySelector(
          '.message-input'
        ) as HTMLTextAreaElement;
        if (messageInput) {
          messageInput.focus();
        }
      }
    }, 300);

    this.trackAnalytics('ai_chat_opened', 'Floating Button');

    if (this.isMobile) {
      document.body.style.overflow = 'hidden';
    }
  }

  private redirectToLogin(): void {
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl: this.router.url,
        feature: 'ai-chat',
      },
    });
  }

  handleChatClose(): void {
    this.closeChat();
  }

  closeChat(): void {
    this.isChatOpen = false;
    this.isMinimized = false;
    this.isOpening = false;

    if (this.isMobile) {
      document.body.style.overflow = '';
    }

    setTimeout(() => {
      if (this.floatingButton) {
        this.floatingButton.nativeElement.focus();
      }
    }, 100);

    this.trackAnalytics('ai_chat_closed', 'User Action');
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    this.trackAnalytics(
      'ai_chat_minimized',
      this.isMinimized ? 'Minimized' : 'Maximized'
    );
  }

  hideWelcomeTooltip(): void {
    this.showWelcomeTooltip = false;
    try {
      localStorage.setItem('ai-chat-welcome-seen', 'true');
    } catch (e) {
      console.warn('Failed to save welcome tooltip state:', e);
    }

    if (this.autoHideTooltipTimer) {
      clearTimeout(this.autoHideTooltipTimer);
    }
  }

  showServiceUnavailableTooltip(): void {
    if (this.serviceUnavailableTooltipTimer) {
      clearTimeout(this.serviceUnavailableTooltipTimer);
    }

    this.isServiceUnavailableTooltipVisible = true;

    this.serviceUnavailableTooltipTimer = window.setTimeout(() => {
      this.isServiceUnavailableTooltipVisible = false;
    }, 3000);
  }

  hideServiceUnavailableTooltip(): void {
    if (this.serviceUnavailableTooltipTimer) {
      clearTimeout(this.serviceUnavailableTooltipTimer);
    }
    this.isServiceUnavailableTooltipVisible = false;
  }

  private trackAnalytics(eventName: string, eventLabel: string): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', eventName, {
        event_category: 'AI Chat',
        event_label: eventLabel,
        value: 1,
      });
    }
  }
}
